import React, { useContext } from "react";
import { Line } from "react-chartjs-2";
import { getLabelsFromDataPoint } from "../outboundAmzl/util/Util";
import { SF_STOW_WIP_THRESHOLD } from "../overview/utils/Constants";
import { PVAOverviewContext } from "../overview/context/PVAOverviewContext";
import { CycleFilterContext } from "../overview/context/CycleFilterContext";
import { getStowWIPChartData } from "../overview/utils/utils";

const PVAChart = (data) => {
  const { settingsInsights, opsClockData, isLMOCSEnabledForOpsClockData } =
    data.breaksDataSources;
  const { pvaData } = useContext(PVAOverviewContext);
  const { cycles } = useContext(CycleFilterContext);
  const { targetData = {} } = pvaData;
  const selectedCycle = cycles.selectedCycles ? cycles.selectedCycles[0] : "";

  const getLabels = () => {
    if (data.processName === "outbound") {
      const pickData = data.pvaData.pvaData.pick;
      return pickData.flowPVAData
        ? getLabelsFromDataPoint(
            pickData.flowPVAData[data.pvaData.pvaData.pvaGraphData.granularity]
              .dataPointList,
          )
        : [];
    } else {
      const overviewData = data.pvaData.pvaData.overviewData;
      return overviewData.flowPVAData
        ? getLabelsFromDataPoint(
            overviewData.flowPVAData[
              data.pvaData.pvaData.pvaGraphData.granularity
            ][0].dataPointList,
          )
        : [];
    }
  };

  const getStowWipLabels = () => {
    const wipData = data.pvaData.pvaData.wip;
    return wipData
      ? getLabelsFromDataPoint(data.pvaData.pvaData.wip.dataPointList)
      : [];
  };
  const stowWIpCheck = () => {
    return (
      data.pvaData.pvaData.pvaGraphData.processName[0] === "stowWip" &&
      data.pvaData.pvaData.wipTargetData &&
      Object.keys(data.pvaData.pvaData.wipTargetData).length > 0
    );
  };

  const options = {
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    scales: {
      xAxes: [
        {
          gridLines: {
            drawOnChartArea: false,
          },
        },
      ],
      yAxes: [
        {
          gridLines: {
            drawOnChartArea: false,
          },
          ticks: {
            autoSkip: true,
            maxTicksLimit: 7,
          },
        },
      ],
    },
  };

  const stowWIPUpperLimit = data.pvaData.pvaData.wipTargetData
    ? +data.pvaData.pvaData.wipTargetData.STOW_WIP_UPPER + SF_STOW_WIP_THRESHOLD
    : SF_STOW_WIP_THRESHOLD;

  /* SF_STOW_WIP_THRESHOLD - capped value to fix Stow WIP Sortation Flow for inflated scenarios (e.g. Heavy Induct with little Stow FPH) */
  return (
    <div>
      {stowWIpCheck() ? (
        <Line
          data={{
            labels: getStowWipLabels(),
            datasets: [
              {
                data: data.pvaData.pvaData.wip.dataPointList
                  ? getStowWIPChartData(
                      data.pvaData.pvaData.wip.dataPointList,
                      {
                        isLMOCSEnabledForOpsClockData,
                        settingsInsights,
                        opsClockData,
                        targetData,
                        selectedCycle,
                      },
                      stowWIPUpperLimit,
                    ).map((dataPoint) => dataPoint.metricValue)
                  : [],
                fill: false,
                label: "Stow WIP",
                borderColor: "#FF8F00",
              },
              {
                data: [
                  {
                    x: getStowWipLabels()[0],
                    y: data.pvaData.pvaData.wipTargetData["STOW_WIP_LOWER"],
                  },
                  {
                    x: getStowWipLabels()[getStowWipLabels().length - 1],
                    y: data.pvaData.pvaData.wipTargetData["STOW_WIP_LOWER"],
                  },
                ],
                fill: false,
                label: "Stow WIP Lower",
                borderColor: "#CB330B",
                borderDash: [7],
                borderWidth: 1,
              },
              {
                data: [
                  {
                    x: getStowWipLabels()[0],
                    y: data.pvaData.pvaData.wipTargetData["STOW_WIP_UPPER"],
                  },
                  {
                    x: getStowWipLabels()[getStowWipLabels().length - 1],
                    y: data.pvaData.pvaData.wipTargetData["STOW_WIP_UPPER"],
                  },
                ],
                fill: false,
                label: "Stow WIP Upper",
                borderColor: "#DCB700",
                borderDash: [7],
                borderWidth: 1,
              },
            ],
          }}
          options={options}
        />
      ) : (
        <Line
          data={{
            labels: getLabels(),
            datasets: data.pvaData.pvaData.pvaGraphData.processName.map(
              (process) => {
                if (process === "inbound") {
                  return {
                    data: data.pvaData.pvaData.inbound
                      ? data.pvaData.pvaData.inbound.flowPVAData[
                          data.pvaData.pvaData.pvaGraphData.granularity
                        ][0].dataPointList.map(
                          (dataPoint) => dataPoint.metricValue,
                        )
                      : [],
                    fill: false,
                    label: "inbound",
                    borderColor: "#FF7494",
                  };
                }
                if (process === "induct") {
                  return {
                    data: data.pvaData.pvaData.overviewData.flowPVAData[
                      data.pvaData.pvaData.pvaGraphData.granularity
                    ][0].dataPointList.map(
                      (dataPoint) => dataPoint.metricValue,
                    ),
                    fill: false,
                    label: "induct",
                    borderColor: "#00A8E1",
                  };
                }
                if (process === "sort") {
                  return {
                    data: data.pvaData.pvaData.overviewData.flowPVAData[
                      data.pvaData.pvaData.pvaGraphData.granularity
                    ][2].dataPointList.map(
                      (dataPoint) => dataPoint.metricValue,
                    ),
                    fill: false,
                    label: "sort",
                    borderColor: "#ADE422",
                  };
                }
                if (process === "pick") {
                  return {
                    data: data.pvaData.pvaData.pick.flowPVAData
                      ? data.pvaData.pvaData.pick.flowPVAData[
                          data.pvaData.pvaData.pvaGraphData.granularity
                        ].dataPointList.map(
                          (dataPoint) => dataPoint.metricValue,
                        )
                      : [],
                    fill: false,
                    label: "pick",
                    borderColor: "#FF7494",
                  };
                }
                if (process === "stage") {
                  return {
                    data: data.pvaData.pvaData.stage.flowPVAData
                      ? data.pvaData.pvaData.stage.flowPVAData[
                          data.pvaData.pvaData.pvaGraphData.granularity
                        ].dataPointList.map(
                          (dataPoint) => dataPoint.metricValue,
                        )
                      : [],
                    fill: false,
                    label: "stage",
                    borderColor: "#00A8E1",
                  };
                }
                return [];
              },
            ),
          }}
          options={options}
        />
      )}
    </div>
  );
};

export default PVAChart;



// WEBPACK FOOTER //
// ./src/components/chart/PVAChartv2.js